<template>
  <main class="l-main p-summary is-ua">
    <TitleMain
      icon="sitesummary"
      faq="/sections/360001202296"
      howTo="/sections/4404626188815"
    >
      <span class="title">GA・GSCデータサマリー</span>
      <span class="description"
        >登録したGoogleアナリティクス・サーチコンソールのデータを元に表示します。</span
      >
    </TitleMain>
    <div class="l-container">
      <div class="l-toolBlock">
        <ButtonToProjects />
        <GaTypeStatus v-if="!isGaTypeError" gaType="ua" :project="project" />
        <Breadcrumb />
      </div>
      <section class="p-summary-summaries">
        <div class="l-block">
          <div class="l-block__header">
            <TitleSub icon="sitesummary" level="h2">
              <span class="title">全体サマリー</span>
              <Tooltip size="lg">
                GAのデータを元に、登録サイトへの新規訪問率やセッション数などの、データの増減を時系列で見ることができます。
              </Tooltip>
            </TitleSub>
            <Calendar
              :range="summary.period.range"
              :value="summary.period.value"
              class="l-ml-2"
              @input="updateSummaryPeriod"
            />
            <RadioGroup variant="btns" class="l-ml-2">
              <Radio
                id="summaryDays"
                name="summaryTimeType"
                value="days"
                variant="btn"
                :checked="summary.timeType"
                @change="updateSummaryTimeType"
              >
                日
              </Radio>
              <Radio
                id="summaryWeek"
                name="summaryTimeType"
                value="week"
                variant="btn"
                :checked="summary.timeType"
                @change="updateSummaryTimeType"
              >
                週
              </Radio>
              <Radio
                id="summaryMonth"
                name="summaryTimeType"
                value="month"
                variant="btn"
                :checked="summary.timeType"
                @change="updateSummaryTimeType"
              >
                月
              </Radio>
            </RadioGroup>
          </div>
          <Tab>
            <TabItem
              v-for="device in devices"
              :key="device.id"
              :id="device.id"
              :active="summary.device"
              @click="updateSummaryDevice"
            >
              <Icon2 v-if="device.id !== 'all'" :variant="device.id" />
              {{ device.label }}
            </TabItem>
          </Tab>
          <div class="l-block__content">
            <Error variant="inline" v-if="isGaTypeError" size="sm">
              <template slot="title">
                データが取得できませんでした。<br />
                ページを再読み込みしてください。
              </template>
              <Button variant="border" size="w80" fsize="f11" @click="reload">
                <Icon variant="return_gn" :size="12" />
                リロード
              </Button>
            </Error>
            <template v-else>
              <ListMetrics
                :checked="summary.checked"
                gaType="ua"
                :items="summary.data.metrics.items"
                :itemData="summary.itemData"
                :period="summary.period.value"
                :isLoading="summary.isLoading"
                class="l-mb-1"
                @change="updateSummaryChecked"
              />
              <GraphLine
                :checked="checkedSummary"
                :data="summary.data.term"
                :itemData="summary.itemData"
                :period="summary.period.value"
                :timeType="summary.timeType"
                :isLoading="summary.isLoading"
              />
            </template>
          </div>
        </div>
      </section>
      <section class="p-summary-organic l-mt-4">
        <div class="l-block">
          <div class="l-block__header">
            <TitleSub icon="organic" level="h2">
              <span class="title">オーガニックの流入推移</span>
              <Tooltip size="lg">
                「GAデータ」は1日前からのデータを、「GSCデータ」は3日前からのデータを表示しています。
              </Tooltip>
            </TitleSub>
            <Calendar
              :listType="organic.source"
              :range="organic.period[organic.source].range"
              :value="organic.period[organic.source].value"
              :hasYoY="organic.source === 'gsc'"
              class="l-ml-2"
              @change="updateOrganicType"
              @input="updateOrganicPeriod"
            />
            <RadioGroup variant="btns" class="l-ml-2">
              <Radio
                id="organicDays"
                name="organicTimeType"
                value="days"
                variant="btn"
                :checked="isComparisonOrganic ? 'month' : organic.timeType"
                :disabled="isComparisonOrganic"
                @change="updateOrganicTimeType"
              >
                日
              </Radio>
              <Radio
                id="organicWeek"
                name="organicTimeType"
                value="week"
                variant="btn"
                :checked="isComparisonOrganic ? 'month' : organic.timeType"
                :disabled="isComparisonOrganic"
                @change="updateOrganicTimeType"
              >
                週
              </Radio>
              <Radio
                id="organicMonth"
                name="organicTimeType"
                value="month"
                variant="btn"
                :checked="isComparisonOrganic ? 'month' : organic.timeType"
                :disabled="isComparisonOrganic"
                @change="updateOrganicTimeType"
              >
                月
              </Radio>
            </RadioGroup>
          </div>
          <Tab>
            <TabItem
              v-for="item in devices"
              :key="item.id"
              :id="item.id"
              :active="organic.device"
              @click="updateOrganicDevice"
            >
              <Icon2 v-if="item.id !== 'all'" :variant="item.id" />
              {{ item.label }}
            </TabItem>
            <div class="c-tab__item c-tab__item--free">
              <ButtonDownloadOrganicGsc
                v-if="organic.source === 'gsc'"
                :device="organic.device"
                :from="
                  isComparisonOrganic
                    ? organic.period.comparison.current[0]
                    : organic.period.gsc.value[0]
                "
                :project="project"
                :to="
                  isComparisonOrganic
                    ? organic.period.comparison.current[1]
                    : organic.period.gsc.value[1]
                "
                :type="isComparisonOrganic ? 'yearComparison' : 'term'"
                :timeType="isComparisonOrganic ? null : organic.timeType"
                class="l-mr-2"
              >
                <span>CSVダウンロード</span>
              </ButtonDownloadOrganicGsc>
              <ButtonToggle
                :items="source"
                :value="organic.source"
                :disabled="organic.isLoading"
                @change="changeOrganicSource"
              >
              </ButtonToggle>
            </div>
          </Tab>
          <div class="l-block__content">
            <Error
              variant="inline"
              v-if="isGaTypeError && organic.source === 'ga'"
              size="sm"
            >
              <template slot="title">
                データが取得できませんでした。<br />
                ページを再読み込みしてください。
              </template>
              <Button variant="border" size="w80" fsize="f11" @click="reload">
                <Icon variant="return_gn" :size="12" />
                リロード
              </Button>
            </Error>
            <template v-else>
              <ListMetrics
                :checked="organic.checked"
                gaType="ua"
                :items="organic.data.metrics.items"
                :itemData="organic.itemData[organic.source]"
                :period="organic.period[organic.source].value"
                :typeType="organic.timeType"
                :isLoading="organic.isLoading"
                class="l-mb-1"
                @change="updateOrganicChecked"
              />
              <template v-if="isComparisonOrganic">
                <ul
                  class="c-graph-legend"
                  :class="{
                    'is-transparent': !checkedOrganic.length
                  }"
                >
                  <li
                    class="c-graph-legend__item c-graph-legend__item--nofigure"
                  >
                    <span
                      class="c-graph-legend__figure"
                      :class="{
                        ['c-color-bg--' +
                        organic.itemData.gsc.impressions.color]: true,
                        'is-hidden':
                          checkedOrganic.indexOf('impressions') === -1
                      }"
                    ></span>
                    <span
                      class="c-graph-legend__figure"
                      :class="{
                        ['c-color-bg--' +
                        organic.itemData.gsc.clicks.color]: true,
                        'is-hidden': checkedOrganic.indexOf('clicks') === -1
                      }"
                    ></span>
                    <span
                      class="c-graph-legend__figure c-graph-legend__figure--solid"
                      :class="{
                        ['c-color-bg--' + organic.itemData.gsc.ctr.color]: true,
                        'is-hidden': checkedOrganic.indexOf('ctr') === -1
                      }"
                    ></span>
                    <span
                      class="c-graph-legend__figure c-graph-legend__figure--solid"
                      :class="{
                        ['c-color-bg--' +
                        organic.itemData.gsc.rank.color]: true,
                        'is-hidden': checkedOrganic.indexOf('rank') === -1
                      }"
                    ></span>
                    今年度
                  </li>
                  <li
                    class="c-graph-legend__item c-graph-legend__item--nofigure"
                  >
                    <span
                      class="c-graph-legend__figure c-graph-legend__figure--prev"
                      :class="{
                        'is-hidden':
                          checkedOrganic.indexOf('impressions') === -1 &&
                          checkedOrganic.indexOf('clicks') === -1
                      }"
                    ></span>
                    <span
                      class="c-graph-legend__figure c-graph-legend__figure--dashed"
                      :class="{
                        ['c-color-bg--' + organic.itemData.gsc.ctr.color]: true,
                        'is-hidden': checkedOrganic.indexOf('ctr') === -1
                      }"
                    ></span>
                    <span
                      class="c-graph-legend__figure c-graph-legend__figure--dashed"
                      :class="{
                        ['c-color-bg--' +
                        organic.itemData.gsc.rank.color]: true,
                        'is-hidden': checkedOrganic.indexOf('rank') === -1
                      }"
                    ></span>
                    前年度
                  </li>
                </ul>
                <GraphComparison
                  v-if="isComparisonOrganic"
                  :checked="checkedOrganic"
                  :data="organic.data.comparison"
                  :itemData="organic.itemData[organic.source]"
                  :period="organic.period.comparison"
                  :isLoading="organic.isLoading"
                />
              </template>
              <template v-else>
                <GraphLine
                  :checked="checkedOrganic"
                  :data="organic.data.term"
                  :itemData="organic.itemData[organic.source]"
                  :period="organic.period[organic.source].value"
                  :timeType="organic.timeType"
                  :isLoading="organic.isLoading"
                />
              </template>
            </template>
          </div>
        </div>
      </section>
      <section class="p-summary-keyword l-mt-4">
        <div class="l-block">
          <div class="l-block__header">
            <TitleSub level="h2">
              <Icon2 variant="pen-circle" :size="36" color="main" />
              指名・一般ワード流入の推移
            </TitleSub>
            <p v-if="keyword.period.length" class="c-text--date">
              {{ keyword.period[0] | dateText('YYYY.MM') }} -
              {{ keyword.period[1] | dateText('YYYY.MM') }}
            </p>
          </div>
          <div class="l-block__content">
            <TitleSub level="h3">
              <Label v-if="keyword.list.items.length" size="lg">設定中</Label>
              <span class="title">指名設定キーワード</span>
            </TitleSub>
            <div
              v-if="keyword.list.items.length"
              class="p-summary-keyword-list"
            >
              <div class="p-summary-keyword-list__content">
                <ul class="c-list--tag">
                  <li
                    v-for="item in keyword.list.items"
                    :key="item.keyword"
                    class="c-list--tag__item"
                  >
                    {{ item.keyword }}
                  </li>
                </ul>
                <p class="l-mt-2">
                  上記のキーワードで「フレーズ一致」したものをカウントします。
                </p>
              </div>
              <div class="p-summary-keyword-list__btn">
                <Button
                  size="w240"
                  variant="main"
                  @click="openNominatedKeywordModal"
                >
                  確認・変更する
                </Button>
              </div>
            </div>
            <Hint v-else-if="!keyword.list.isLoading">
              <template slot="title">
                Hint!
              </template>
              <p>
                まずは、指名キーワードを登録しましょう。
              </p>
              <template slot="btn">
                <Button
                  size="w240"
                  fsize="f16"
                  variant="main"
                  icon="arrow_r"
                  @click="openNominatedKeywordModal"
                >
                  指名キーワードの登録
                </Button>
              </template>
            </Hint>
            <ul class="c-graph-legend">
              <li class="c-graph-legend__item c-graph-legend__item--general">
                一般
              </li>
              <li class="c-graph-legend__item">指名</li>
            </ul>
            <GraphStack
              :data="keyword.data"
              :keywords="keyword.list.items"
              :period="keyword.period"
              :isLoading="keyword.isLoading"
            />
          </div>
        </div>
      </section>
      <Modal
        v-if="keyword.isOpenModal"
        variant="nominated-keyword"
        @close="closeNominatedKeywordModal"
      >
        <template slot="title">
          指名キーワードの登録・設定
        </template>
        <template slot="content">
          <FormAddNominatedKeyword
            :items="keyword.list.items"
            :project="project"
            :isLoading="keyword.list.isLoading"
            @add="addNominatedKeyword"
          />
          <template v-if="keyword.list.items.length">
            <h2 class="hTitle">登録されている指名キーワードの一覧</h2>
            <table class="c-table edit">
              <thead>
                <tr>
                  <td width="136">登録日</td>
                  <td>キーワード</td>
                  <td width="69">削除</td>
                </tr>
              </thead>
              <tbody>
                <FormEditNominatedKeywords
                  v-for="(item, index) in keyword.list.items"
                  :key="item.id"
                  :index="index"
                  :item="item"
                  :items="keyword.list.items"
                  :project="project"
                  :isLoading="keyword.list.isLoading"
                  @delete="deleteNominatedKeyword"
                  @edit="editNominatedKeyword"
                />
              </tbody>
            </table>
          </template>
        </template>
      </Modal>
    </div>
  </main>
</template>

<script>
import Button from '@/components/Button';
import ButtonToProjects from '@/components/ButtonToProjects';
import ButtonDownloadOrganicGsc from '@/components/siteSummary/ButtonDownloadOrganicGsc';
import ButtonToggle from '@/components/ButtonToggle';
import Calendar from '@/components/Calendar';
import Label from '@/components/Label';
import Error from '@/components/Error';
import FormAddNominatedKeyword from '@/components/config/FormAddNominatedKeyword';
import FormEditNominatedKeywords from '@/components/config/FormEditNominatedKeywords';
import GaTypeStatus from '@/components/GaTypeStatus';
import GraphComparison from '@/components/siteSummary/GraphComparison';
import GraphLine from '@/components/siteSummary/GraphLine';
import GraphStack from '@/components/siteSummary/GraphStack';
import Hint from '@/components/Hint';
import Icon from '@/components/Icon';
import Icon2 from '@/components/Icon2';
import ListMetrics from '@/components/ListMetrics';
import Modal from '@/components/Modal';
import Radio from '@/components/Radio';
import RadioGroup from '@/components/RadioGroup';
import Tab from '@/components/Tab';
import TabItem from '@/components/TabItem';
import TitleMain from '@/components/TitleMain';
import Breadcrumb from '@/components/Breadcrumb';
import TitleSub from '@/components/TitleSub';

import Tooltip from '@/components/Tooltip';
import apiProjects from '@/api/projects';
import axios from 'axios';
import utils from '@/utils';
import * as dayjs from 'dayjs';

export default {
  props: {
    project: {
      type: Object,
      required: true
    },
    isGaTypeError: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  components: {
    Breadcrumb,
    Button,
    ButtonToProjects,
    ButtonDownloadOrganicGsc,
    ButtonToggle,
    Calendar,
    Label,
    Error,
    FormAddNominatedKeyword,
    FormEditNominatedKeywords,
    GaTypeStatus,
    GraphComparison,
    GraphLine,
    GraphStack,
    Hint,
    Icon,
    Icon2,
    ListMetrics,
    Modal,
    Radio,
    RadioGroup,
    Tab,
    TabItem,
    TitleMain,
    TitleSub,
    Tooltip
  },

  data() {
    return {
      devices: [
        {
          id: 'all',
          label: '全体'
        },
        {
          id: 'desktop',
          label: 'PC'
        },
        {
          id: 'mobile',
          label: 'スマホ'
        },
        {
          id: 'tablet',
          label: 'タブレット'
        }
      ],
      source: [
        {
          label: 'GAデータ',
          value: 'ga'
        },
        {
          label: 'GSCデータ',
          value: 'gsc'
        }
      ],
      summary: {
        cancelTokens: [],
        checked: this.$store.state.page.siteSummary.summaryChecked,
        data: {
          metrics: {
            items: {}
          },
          term: {
            items: []
          }
        },
        device: this.$store.state.page.siteSummary.summaryDevice,
        itemData: {
          session: {
            color: 'orange',
            icon: 'session',
            label: 'セッション数',
            type: 'number'
          },
          user: {
            color: 'green',
            icon: 'user',
            label: 'ユーザー数',
            type: 'number',
            unit: '人'
          },
          visitCount: {
            color: 'pink',
            icon: 'new',
            label: '新規訪問数',
            type: 'number',
            unit: '人'
          },
          visitRate: {
            color: 'purple',
            icon: 'percent',
            label: '新規訪問率',
            type: 'percentage',
            unit: '%'
          },
          bounceRate: {
            color: 'yellow',
            icon: 'bounce',
            label: '直帰率',
            type: 'percentage',
            unit: '%'
          },
          migration: {
            color: 'blue',
            icon: 'reload',
            label: '回遊数（PV/SS）',
            type: 'number'
          },
          duration: {
            color: 'yellow-green',
            icon: 'time',
            label: '平均滞在時間',
            type: 'time'
          },
          conversion: {
            color: 'light-blue',
            icon: 'conversion',
            label: 'コンバージョン合計値',
            type: 'number'
          }
        },
        period: {
          range: [],
          value: []
        },
        timeType: 'days',
        isLoading: true
      },
      organic: {
        cancelTokens: [],
        checked: this.$store.state.page.siteSummary.organicChecked,
        data: {
          comparison: {
            current: [],
            prev: []
          },
          metrics: {
            items: {}
          },
          term: {
            items: []
          }
        },
        device: this.$store.state.page.siteSummary.organicDevice,
        itemData: {
          ga: {
            session: {
              color: 'orange',
              icon: 'session',
              label: 'セッション数',
              type: 'number'
            },
            user: {
              color: 'green',
              icon: 'user',
              label: 'ユーザー数',
              type: 'number',
              unit: '人'
            },
            visitCount: {
              color: 'pink',
              icon: 'new',
              label: '新規訪問数',
              type: 'number',
              unit: '人'
            },
            visitRate: {
              color: 'purple',
              icon: 'percent',
              label: '新規訪問率',
              type: 'percentage',
              unit: '%'
            },
            conversion: {
              color: 'light-blue',
              icon: 'conversion',
              label: 'コンバージョン合計値',
              type: 'number'
            }
          },
          gsc: {
            impressions: {
              color: 'orange',
              icon: 'imp',
              label: '表示回数',
              type: 'number',
              unit: '回'
            },
            clicks: {
              color: 'green',
              icon: 'mouse',
              label: 'クリック数',
              type: 'number',
              unit: '回'
            },
            ctr: {
              color: 'pink',
              icon: 'percent',
              label: 'クリック率',
              type: 'percentage',
              unit: '%'
            },
            rank: {
              color: 'purple',
              icon: 'rank_change',
              label: '平均順位',
              type: 'rank',
              unit: '位'
            }
          }
        },
        period: {
          comparison: {
            current: [],
            prev: []
          },
          ga: {
            range: [],
            value: []
          },
          gsc: {
            range: [],
            value: []
          }
        },
        source: 'ga',
        timeType: 'days',
        type: 'default',
        isLoading: true
      },
      keyword: {
        cancelToken: null,
        data: {
          items: []
        },
        list: {
          cancelToken: null,
          items: [],
          isEdited: false,
          isLoading: true
        },
        period: [],
        isLoading: true,
        isOpenModal: false
      },
      startTime: 0
    };
  },

  computed: {
    checkedOrganic() {
      // checkedにはGAとGSCの両方の項目が入ってくるので絞り込む
      const pathes = Object.keys(this.organic.itemData[this.organic.source]);
      return this.organic.checked.filter(check => pathes.indexOf(check) !== -1);
    },
    checkedSummary() {
      // checkedにはGA4とUAの両方の項目が入ってくるので絞り込む
      const pathes = Object.keys(this.summary.itemData);
      return this.summary.checked.filter(check => pathes.indexOf(check) !== -1);
    },
    isComparisonOrganic() {
      return this.organic.type === 'comparison';
    },
    summaryPeriodInfoKey() {
      return `${this.project.id}-summaryPeriodInfo`;
    },
    organicGAPeriodInfoKey() {
      return `${this.project.id}-organicGAPeriodInfo`;
    },
    organicGSCPeriodInfoKey() {
      return `${this.project.id}-organicGSCPeriodInfo`;
    }
  },

  async created() {
    if (this.isSpanDays(this.summaryPeriodInfoKey)) {
      localStorage.removeItem(this.summaryPeriodInfoKey);
    }

    if (this.isSpanDays(this.organicGSCPeriodInfoKey)) {
      localStorage.removeItem(this.organicGSCPeriodInfoKey);
    }

    if (this.isSpanDays(this.organicGAPeriodInfoKey)) {
      localStorage.removeItem(this.organicGAPeriodInfoKey);
    }

    // 環境がdemoの場合はデモ用の表示期間で固定するようにする
    if (process.env.VUE_APP_ENV === 'demo') {
      this.summary.period.range = utils.getGADemoPeriod('all');
      this.summary.period.value = utils.getGADemoPeriod();
    } else if (localStorage.getItem(this.summaryPeriodInfoKey)) {
      this.summary.period.range = utils.getGAPeriod('all');
      this.summary.period.value = this.convertStringToDate(
        JSON.parse(localStorage.getItem(this.summaryPeriodInfoKey)).value
      );
    } else {
      this.summary.period.range = utils.getGAPeriod('all');
      this.summary.period.value = utils.getGAPeriod();
    }
    this.getSummaryData();

    if (this.isGaTypeError) {
      // エラー時にオーガニック流入推移をGSCに切り替えられるようにローディング状態を解除する
      this.organic.isLoading = false;
    }
    this.updateOrganicPeriodBySource();
    this.getOrganicData();

    // 一般指名キーワードの流入数もデモの場合は期間を固定にする
    // 環境がdemoの場合はデモ用の表示期間で固定するようにする
    if (process.env.VUE_APP_ENV === 'demo') {
      // GSCのデモ用の期間を仕様
      let to = utils.getGADemoPeriod('all')[1];
      to = utils.getStartDate(to, 'month');
      let from = utils.getDateResetHours(to);
      from.setFullYear(from.getFullYear() - 1);
      this.keyword.period = [from, to];
    } else {
      let to = utils.getDateResetHours();
      to.setDate(to.getDate() - 1);
      to = utils.getStartDate(to, 'month');
      let from = utils.getDateResetHours(to);
      from.setFullYear(from.getFullYear() - 1);
      this.keyword.period = [from, to];
    }
    this.getNominatedKeywords();
    this.getNominatedKeywordData();
    // 描画されたタイミングをミリ秒で記録
    this.startTime = Date.now();
  },

  beforeDestroy() {
    this.cancelSummaryRequests();
    this.cancelOrganicRequests();
    this.cancelKeywordRequests();

    if (process.env.VUE_APP_ENV === 'demo') {
      return;
    }
    const endTime = Date.now();
    // 滞在時間を秒単位でカウント
    const duration = (endTime - this.startTime) / 1000;
    const properties = {
      duration: duration
    };
    amplitude
      .getInstance()
      .logEvent('Duration of Visits - Site Summary', properties);
  },

  methods: {
    cancelSummaryRequests() {
      this.summary.cancelTokens.forEach(cancelToken => {
        cancelToken.cancel();
      });
      this.summary.cancelTokens = [];
    },
    getSummaryData() {
      if (this.isGaTypeError) {
        return;
      }
      this.cancelSummaryRequests();
      this.summary.isLoading = true;

      Promise.all([this.getSummaryMetrics(), this.getSummaryTerms()])
        .then(response => {
          this.summary.data.metrics.items = response[0].data.items;
          const items = [];
          const total = response[1].data.items.length;
          const startDate = utils.getStartDate(
            this.summary.period.value[0],
            this.summary.timeType
          );
          const endDate = utils.getEndDate(
            this.summary.period.value[1],
            this.summary.timeType
          );

          for (let i = 0; i < total; i++) {
            const item = response[1].data.items[i];
            const date = utils.getDateResetHours(item.date);
            // 指定期間範囲内のデータのみitemsに入れる
            if (date >= startDate && date <= endDate) {
              item.date = utils.getStartDate(date, this.summary.timeType);
              if (item.date < this.summary.period.value[0]) {
                item.date = new Date(this.summary.period.value[0]);
              }
              if (item.visitRate) {
                item.visitRate *= 100;
              }
              if (item.bounceRate) {
                item.bounceRate *= 100;
              }
              if (item.engagementRate) {
                item.engagementRate *= 100;
              }
              // durationは"0:2:42"でengagementAverageTimeは秒数で返ってくる
              // durationを秒数に変換する
              if (item.duration && item.duration.indexOf(':') !== -1) {
                item.duration = this.$options.filters.clockToSeconds(
                  item.duration
                );
              }
              items.push(item);
            }
          }
          this.summary.data.term.items = items;
          this.summary.isLoading = false;
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.summary.data.metrics.items = {};
            this.summary.data.term.items = [];
            this.summary.isLoading = false;
          }
        });
    },
    getSummaryMetrics() {
      let params = {
        device: this.summary.device,
        from: utils.formatDate(this.summary.period.value[0]),
        to: utils.formatDate(this.summary.period.value[1])
      };
      const cancelToken = axios.CancelToken.source();
      this.summary.cancelTokens.push(cancelToken);

      return apiProjects.metricsSummaries.get(
        params,
        this.auth.idToken,
        this.project.id,
        cancelToken.token
      );
    },
    getSummaryTerms() {
      let params = {
        device: this.summary.device,
        from: utils.formatDate(this.summary.period.value[0]),
        to: utils.formatDate(this.summary.period.value[1]),
        summaryType: this.summary.timeType
      };
      let type = null;
      if (this.summary.timeType !== 'days') {
        type = this.summary.timeType;
      }
      const cancelToken = axios.CancelToken.source();
      this.summary.cancelTokens.push(cancelToken);

      return apiProjects.termSummaries.get(
        params,
        this.auth.idToken,
        this.project.id,
        cancelToken.token,
        type
      );
    },
    updateSummaryChecked(checked) {
      this.summary.checked = checked;
      this.setStatus({
        summaryChecked: checked
      });
    },
    updateSummaryDevice(id) {
      this.summary.device = id;
      this.setStatus({
        summaryDevice: id
      });
      this.getSummaryData();
    },
    updateSummaryPeriod(value) {
      this.summary.period.value = value;
      const expireDate = dayjs().endOf('day');
      localStorage.setItem(
        this.summaryPeriodInfoKey,
        JSON.stringify({ value, expireDate })
      );
      this.getSummaryData();
    },
    updateSummaryTimeType(value) {
      this.summary.timeType = value;
      this.getSummaryData();
    },

    getComparisonPeriod(period) {
      let prevFrom = new Date(period[0]);
      let prevTo = new Date(period[0]);
      let currentFrom = new Date(period[1]);
      let currentTo = new Date(period[1]);
      prevTo.setMonth(prevTo.getMonth() + 12);
      prevTo.setDate(prevTo.getDate() - 1);
      currentFrom.setMonth(currentFrom.getMonth() - 12);
      currentFrom.setDate(currentFrom.getDate() + 1);
      return {
        current: [currentFrom, currentTo],
        prev: [prevFrom, prevTo]
      };
    },

    cancelOrganicRequests() {
      this.organic.cancelTokens.forEach(cancelToken => {
        cancelToken.cancel();
      });
      this.organic.cancelTokens = [];
    },
    getOrganicData() {
      if (this.isGaTypeError && this.organic.source === 'ga') {
        return;
      }
      this.cancelOrganicRequests();
      this.organic.isLoading = true;

      let requests = [this.getOrganicMetrics()];
      if (this.isComparisonOrganic) {
        requests.push(this.getOrganicTerms('now'));
        requests.push(this.getOrganicTerms('old'));
      } else {
        requests.push(this.getOrganicTerms('now'));
      }

      Promise.all(requests)
        .then(response => {
          this.organic.data.metrics.items = response[0].data.items;
          if (this.isComparisonOrganic) {
            this.organic.data.comparison.current = response[1].data.items;
            this.organic.data.comparison.prev = response[2].data.items;
          } else {
            const items = [];
            const total = response[1].data.items.length;
            const period = this.organic.period[this.organic.source];
            const startDate = utils.getStartDate(
              period.value[0],
              this.organic.timeType
            );
            const endDate = utils.getEndDate(
              period.value[1],
              this.organic.timeType
            );
            for (let i = 0; i < total; i++) {
              const item = response[1].data.items[i];
              const date = utils.getDateResetHours(item.date);
              // 指定期間範囲内のデータのみitemsに入れる
              if (date >= startDate && date <= endDate) {
                item.date = utils.getStartDate(date, this.organic.timeType);
                if (item.date < period.value[0]) {
                  item.date = new Date(period.value[0]);
                }
                if (item.visitRate) {
                  item.visitRate *= 100;
                } else if (item.ctr) {
                  item.ctr *= 100;
                }
                items.push(item);
              }
            }
            this.organic.data.term.items = items;
          }
          this.organic.isLoading = false;
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.organic.data.metrics.items = {};
            this.organic.data.comparison.current = [];
            this.organic.data.comparison.prev = [];
            this.organic.data.term.items = [];
            this.organic.isLoading = false;
          }
        });
    },
    getOrganicMetrics() {
      const cancelToken = axios.CancelToken.source();
      this.organic.cancelTokens.push(cancelToken);

      if (this.organic.source === 'ga') {
        let params = {
          device: this.organic.device,
          from: utils.formatDate(this.organic.period.ga.value[0]),
          to: utils.formatDate(this.organic.period.ga.value[1])
        };
        return apiProjects.metricsOrganic.get(
          params,
          this.auth.idToken,
          this.project.id,
          cancelToken.token
        );
      } else {
        let params = {
          device: this.organic.device,
          from: utils.formatDate(this.organic.period.gsc.value[0]),
          to: utils.formatDate(this.organic.period.gsc.value[1])
        };
        return apiProjects.searchSummaries.get(
          params,
          this.auth.idToken,
          this.project.id,
          cancelToken.token
        );
      }
    },
    getOrganicTerms(term) {
      let type = null;
      const cancelToken = axios.CancelToken.source();
      this.organic.cancelTokens.push(cancelToken);

      if (this.organic.source === 'ga') {
        let params = {
          device: this.organic.device,
          from: utils.formatDate(this.organic.period.ga.value[0]),
          to: utils.formatDate(this.organic.period.ga.value[1]),
          summaryType: this.organic.timeType
        };
        if (this.organic.timeType !== 'days') {
          type = this.organic.timeType;
        }
        return apiProjects.termOrganic.get(
          params,
          this.auth.idToken,
          this.project.id,
          cancelToken.token,
          type
        );
      } else {
        let params = {
          device: this.organic.device,
          from: utils.formatDate(this.organic.period.gsc.value[0]),
          to: utils.formatDate(this.organic.period.gsc.value[1]),
          summaryType: this.organic.timeType
        };
        if (this.isComparisonOrganic) {
          if (term === 'now') {
            params.from = utils.formatDate(
              this.organic.period.comparison.current[0]
            );
            params.to = utils.formatDate(
              this.organic.period.comparison.current[1]
            );
          } else {
            params.from = utils.formatDate(
              this.organic.period.comparison.prev[0]
            );
            params.to = utils.formatDate(
              this.organic.period.comparison.prev[1]
            );
          }
          params.summaryType = 'days';
        } else if (this.organic.timeType !== 'days') {
          term = null;
          type = this.organic.timeType;
        }
        return apiProjects.searchTerms.get(
          params,
          this.auth.idToken,
          this.project.id,
          cancelToken.token,
          term,
          type
        );
      }
    },
    changeOrganicSource(value) {
      this.organic.source = value;
      this.organic.type = 'default';
      this.updateOrganicPeriodBySource();
      this.getOrganicData();
    },
    updateOrganicChecked(checked) {
      this.organic.checked = checked;
      this.setStatus({ organicChecked: checked });
    },
    updateOrganicDevice(id) {
      this.organic.device = id;
      this.setStatus({ organicDevice: id });
      this.getOrganicData();
    },
    updateOrganicPeriod(value) {
      if (this.organic.source === 'gsc') {
        this.organic.period.gsc.value = value;
        const expireDate = dayjs().endOf('day');
        localStorage.setItem(
          this.organicGSCPeriodInfoKey,
          JSON.stringify({ value, expireDate })
        );
        if (this.isComparisonOrganic) {
          this.organic.period.comparison = this.getComparisonPeriod(
            this.organic.period.gsc.value
          );
        } else {
          this.organic.period.comparison = [];
        }
      } else {
        this.organic.period.ga.value = value;
        const expireDate = dayjs().endOf('day');
        localStorage.setItem(
          this.organicGAPeriodInfoKey,
          JSON.stringify({ value, expireDate })
        );
        if (this.isComparisonOrganic) {
          this.organic.period.comparison = this.getComparisonPeriod(
            this.organic.period.ga.value
          );
        } else {
          this.organic.period.comparison = [];
        }
      }
      this.getOrganicData();
    },
    updateOrganicPeriodBySource() {
      if (this.organic.source === 'gsc') {
        // 環境がdemoの場合はデモ用の表示期間で固定するようにする
        if (process.env.VUE_APP_ENV === 'demo') {
          this.organic.period.gsc.range = utils.getGSCDemoPeriod('all');
          this.organic.period.gsc.value = utils.getGSCDemoPeriod();
        } else if (localStorage.getItem(this.organicGSCPeriodInfoKey)) {
          this.organic.period.gsc.range = utils.getGSCPeriod('all');
          this.organic.period.gsc.value = this.convertStringToDate(
            JSON.parse(localStorage.getItem(this.organicGSCPeriodInfoKey)).value
          );
        } else {
          this.organic.period.gsc.range = utils.getGSCPeriod('all');
          this.organic.period.gsc.value = utils.getGSCPeriod();
        }
      } else {
        // 環境がdemoの場合はデモ用の表示期間で固定するようにする
        if (process.env.VUE_APP_ENV === 'demo') {
          this.organic.period.ga.range = utils.getGADemoPeriod('all');
          this.organic.period.ga.value = utils.getGADemoPeriod();
        } else if (localStorage.getItem(this.organicGAPeriodInfoKey)) {
          this.organic.period.ga.range = utils.getGAPeriod('all');
          this.organic.period.ga.value = this.convertStringToDate(
            JSON.parse(localStorage.getItem(this.organicGAPeriodInfoKey)).value
          );
        } else {
          this.organic.period.ga.range = utils.getGAPeriod('all');
          this.organic.period.ga.value = utils.getGAPeriod();
        }
      }
    },
    updateOrganicType(type) {
      this.organic.type = type;
    },
    updateOrganicTimeType(value) {
      this.organic.timeType = value;
      this.getOrganicData();
    },

    cancelKeywordRequests() {
      this.cancelKeywordDataRequest();
      this.cancelKeywordListRequest();
    },
    cancelKeywordDataRequest() {
      if (this.keyword.cancelToken) {
        this.keyword.cancelToken.cancel();
      }
    },
    cancelKeywordListRequest() {
      if (this.keyword.list.cancelToken) {
        this.keyword.list.cancelToken.cancel();
      }
    },
    getNominatedKeywordData() {
      this.cancelKeywordDataRequest();
      this.keyword.isLoading = true;

      let params = {
        from: utils.formatDate(this.keyword.period[0]),
        to: utils.formatDate(this.keyword.period[1])
      };
      const cancelToken = axios.CancelToken.source();
      this.keyword.cancelToken = cancelToken;

      apiProjects.influxKeywordSummaries
        .get(params, this.auth.idToken, this.project.id, cancelToken.token)
        .then(response => {
          let items = [];
          let total = response.data.items.length;
          for (let i = 0; i < total; i++) {
            let item = response.data.items[i];
            let date = utils.getDateResetHours(item.monthJst);
            if (
              date >= this.keyword.period[0] &&
              date <= this.keyword.period[1]
            ) {
              items.push(item);
            }
          }
          this.keyword.data.items = items;
          this.keyword.isLoading = false;
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.keyword.data.items = [];
            this.keyword.isLoading = false;
          }
        });
    },
    getNominatedKeywords() {
      this.cancelKeywordListRequest();
      this.keyword.list.isLoading = true;
      const cancelToken = axios.CancelToken.source();
      this.keyword.list.cancelToken = cancelToken;

      apiProjects.nominatedKeywords
        .get(this.auth.idToken, this.project.id, cancelToken.token)
        .then(response => {
          this.keyword.list.items = response.data.items;
          this.keyword.list.isLoading = false;
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            this.keyword.list.items = [];
            this.keyword.list.isLoading = false;
          }
        });
    },
    addNominatedKeyword() {
      this.keyword.list.isEdited = true;
      this.getNominatedKeywords();
    },
    deleteNominatedKeyword(index) {
      this.keyword.list.isEdited = true;
      this.keyword.list.items.splice(index, 1);
    },
    editNominatedKeyword() {
      this.keyword.list.isEdited = true;
      this.getNominatedKeywords();
    },
    openNominatedKeywordModal() {
      this.keyword.isOpenModal = true;
    },
    closeNominatedKeywordModal() {
      if (this.keyword.list.isEdited) {
        this.keyword.list.isEdited = false;
        this.getNominatedKeywordData();
      }
      this.keyword.isOpenModal = false;
    },
    convertStringToDate(value) {
      if (Array.isArray(value)) {
        return value.map(item => dayjs(item).$d);
      } else {
        return dayjs(value).$d;
      }
    },
    isSpanDays(periodInfoName) {
      const periodInfo = localStorage.getItem(periodInfoName);
      if (periodInfo) {
        return (
          this.convertStringToDate(JSON.parse(periodInfo).expireDate) <
          dayjs().$d
        );
      }
      return false;
    },

    setStatus(status) {
      this.$store.dispatch('page/setStatus', {
        page: 'siteSummary',
        status: status
      });
    },
    reload() {
      location.reload(true);
    }
  }
};
</script>
